/**
 * Travel ball player and parent/guardian reads. A family view may call only these and the account
 * endpoints (`PATCH /api/auth/me`, the travel ball legal accept): every coach `/api/travel-ball/*`
 * route answers a family view with 403 `travel_ball_family_view`.
 */

import { del, get, patch, post } from './api';
import { normalizeFamilyAnnouncement } from './travelBallAnnouncements';
import { fromApiError } from './travelBallSignup';
import { toRosterInviteError } from './travelBallRosterInvites';
import type { AcceptTravelBallRosterInviteResponse } from '../types/travelBallRosterInvites';
import type { FamilyAnnouncement } from '../types/travelBallAnnouncements';
import type {
  AcceptTravelBallFamilyRosterInviteRequest,
  CreateTravelBallFamilyLinkRequest,
  TravelBallAvailability,
  TravelBallFamilyHome,
  TravelBallFamilyJoinRequest,
  TravelBallFamilyLinkedPerson,
  TravelBallFamilyLinkRequestSent,
  TravelBallFamilyLinks,
  TravelBallGuardianRelationship,
  TravelBallFamilyTeamSearchPage,
  TravelBallFamilyTeamView,
} from '../types/travelBallFamily';

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/**
 * The signed-in family member's home: their name, their teams, linked players and join requests. Missing arrays read as empty, so a partial answer renders the empty home rather
 * than crashing. Failures are thrown as `TravelBallSignupError`.
 */
export const getTravelBallFamilyHome = async (): Promise<TravelBallFamilyHome> => {
  try {
    const home = await get<TravelBallFamilyHome>('/api/travel-ball/family/home');
    return {
      ...home,
      teams: asArray(home?.teams),
      players: asArray(home?.players),
      joinRequests: asArray(home?.joinRequests),
      incomingLinkRequests: asArray(home?.incomingLinkRequests),
      rosterInvites: asArray(home?.rosterInvites),
    };
  } catch (error) {
    throw fromApiError(error);
  }
};

/** The fewest characters the team search accepts; shorter answers 400 `query_too_short`. */
export const TEAM_SEARCH_MIN_CHARS = 2;
export const TEAM_SEARCH_PAGE_SIZE = 20;

/**
 * Join a Team's search: joinable teams whose team or program name contains `q`. Names only, paged.
 * `signal` lets a newer keystroke abandon an older read.
 */
export const searchTravelBallFamilyTeams = async (
  q: string,
  page = 1,
  options: { signal?: AbortSignal; pageSize?: number } = {},
): Promise<TravelBallFamilyTeamSearchPage> => {
  const params = new URLSearchParams({
    q,
    page: String(page),
    pageSize: String(options.pageSize ?? TEAM_SEARCH_PAGE_SIZE),
  });
  try {
    const body = await get<TravelBallFamilyTeamSearchPage>(
      `/api/travel-ball/family/team-search?${params.toString()}`,
      { signal: options.signal },
    );
    return {
      items: asArray(body?.items),
      page: body?.page ?? page,
      pageSize: body?.pageSize ?? TEAM_SEARCH_PAGE_SIZE,
      totalCount: body?.totalCount ?? 0,
      totalPages: body?.totalPages ?? 0,
    };
  } catch (error) {
    throw fromApiError(error);
  }
};

/**
 * The team view for a team the player is on. 404 `team_not_found` otherwise, including while
 * pending. A guardian passes the linked player whose team it is (`playerUserId`); a player leaves it out.
 */
export const getTravelBallFamilyTeam = async (
  teamId: string,
  playerUserId?: string | null,
): Promise<TravelBallFamilyTeamView> => {
  const query = playerUserId ? `?playerUserId=${encodeURIComponent(playerUserId)}` : '';
  try {
    const view = await get<TravelBallFamilyTeamView>(
      `/api/travel-ball/family/teams/${encodeURIComponent(teamId)}${query}`,
    );
    return {
      ...view,
      coaches: asArray(view?.coaches),
      roster: asArray(view?.roster),
      recentAnnouncements: asArray<FamilyAnnouncement>(view?.recentAnnouncements).map(normalizeFamilyAnnouncement),
      // Only an explicit true lets them post: an older answer hides New announcement.
      canAnnounce: view?.canAnnounce === true,
    };
  } catch (error) {
    throw fromApiError(error);
  }
};

/**
 * Ask to join a team. The team's coaches get an email and a badge. A guardian asks for one of their
 * linked players and must name them (`playerUserId`); a player asks for themselves.
 */
export const createTravelBallJoinRequest = async (
  teamId: string,
  playerUserId?: string | null,
): Promise<TravelBallFamilyJoinRequest> => {
  try {
    return await post<TravelBallFamilyJoinRequest>(
      '/api/travel-ball/family/join-requests',
      playerUserId ? { teamId, playerUserId } : { teamId },
    );
  } catch (error) {
    throw fromApiError(error);
  }
};

/** Withdraw a pending request. 409 `already_decided` once a coach has answered it. */
export const cancelTravelBallJoinRequest = async (
  id: string,
): Promise<TravelBallFamilyJoinRequest> => {
  try {
    return await del<TravelBallFamilyJoinRequest>(
      `/api/travel-ball/family/join-requests/${encodeURIComponent(id)}`,
    );
  } catch (error) {
    throw fromApiError(error);
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Linking players and parents/guardians by email (Account)

const linkPath = (linkId: string) => `/api/travel-ball/family/links/${encodeURIComponent(linkId)}`;

/** This account's links: accepted, waiting for this account, and sent by it. Missing lists read as empty. */
export const getTravelBallFamilyLinks = async (): Promise<TravelBallFamilyLinks> => {
  try {
    const body = await get<TravelBallFamilyLinks>('/api/travel-ball/family/links');
    return {
      linked: asArray(body?.linked),
      incoming: asArray(body?.incoming),
      outgoing: asArray(body?.outgoing),
    };
  } catch (error) {
    throw fromApiError(error);
  }
};

/**
 * Ask someone to link by email. Always answers 202 with the same message whether or not an account
 * exists, so the page shows that message and re-reads the links rather than guessing.
 */
export const requestTravelBallFamilyLink = async (
  request: CreateTravelBallFamilyLinkRequest,
): Promise<TravelBallFamilyLinkRequestSent> => {
  try {
    return await post<TravelBallFamilyLinkRequestSent>('/api/travel-ball/family/links', request);
  } catch (error) {
    throw fromApiError(error);
  }
};

/** Accept a link request. A guardian accepting must say how they are related. */
export const acceptTravelBallFamilyLink = async (
  linkId: string,
  relationship?: TravelBallGuardianRelationship,
): Promise<TravelBallFamilyLinkedPerson> => {
  try {
    return await post<TravelBallFamilyLinkedPerson>(
      `${linkPath(linkId)}/accept`,
      relationship ? { relationship } : {},
    );
  } catch (error) {
    throw fromApiError(error);
  }
};

export const declineTravelBallFamilyLink = async (linkId: string): Promise<void> => {
  try {
    await post<void>(`${linkPath(linkId)}/decline`, {});
  } catch (error) {
    throw fromApiError(error);
  }
};

/** A guardian changes how they are related to a linked player. */
export const updateTravelBallFamilyLinkRelationship = async (
  linkId: string,
  relationship: TravelBallGuardianRelationship,
): Promise<TravelBallFamilyLinkedPerson> => {
  try {
    return await patch<TravelBallFamilyLinkedPerson>(linkPath(linkId), { relationship });
  } catch (error) {
    throw fromApiError(error);
  }
};

/**
 * Cancel a request this account sent, or (a guardian only) unlink an accepted player. A player gets
 * 403 `player_cannot_unlink`, so the page never offers it to them.
 */
export const deleteTravelBallFamilyLink = async (linkId: string): Promise<void> => {
  try {
    await del<void>(linkPath(linkId));
  } catch (error) {
    throw fromApiError(error);
  }
};

const rosterInvitePath = (inviteId: string, action: 'accept' | 'decline') =>
  `/api/travel-ball/family/roster-invites/${encodeURIComponent(inviteId)}/${action}`;

/**
 * Accept a coach's roster invite from the Home (#15), exactly as the emailed link's signed-in accept.
 * Refusals are thrown as `RosterInviteError` with the server's code, so the accept page's copy applies.
 */
export const acceptTravelBallFamilyRosterInvite = async (
  inviteId: string,
  request: AcceptTravelBallFamilyRosterInviteRequest = {},
): Promise<AcceptTravelBallRosterInviteResponse> => {
  try {
    return await post<AcceptTravelBallRosterInviteResponse>(rosterInvitePath(inviteId, 'accept'), {
      ...request,
      platform: 'web',
    });
  } catch (error) {
    throw toRosterInviteError(error);
  }
};

/** Decline a coach's roster invite from the Home: it is cancelled. 404 `invite_not_found` once it is not pending. */
export const declineTravelBallFamilyRosterInvite = async (inviteId: string): Promise<void> => {
  try {
    await post<void>(rosterInvitePath(inviteId, 'decline'), {});
  } catch (error) {
    throw toRosterInviteError(error);
  }
};

const AVAILABILITY_TIMEOUT_MS = 8000;

/**
 * Whether travel ball is open to the public, read anonymously so the login page can offer family
 * signup only when it will work. **Fails closed**: a 404 from an API older than the endpoint, a
 * network error, a timeout or a malformed body all read as `false`.
 */
export const getTravelBallAvailability = async (): Promise<boolean> => {
  try {
    const body = await get<TravelBallAvailability>('/api/public/travel-ball/availability', {
      timeout: AVAILABILITY_TIMEOUT_MS,
    });
    return body?.enabled === true;
  } catch {
    return false;
  }
};
